<!-- eslint-disable-next-line vue/max-lines-per-block -->
<script setup lang="ts" generic="T extends string | { name: string; image?: string }">
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/vue'
import { ExclamationCircleIcon, PlusCircleIcon } from '@heroicons/vue/24/outline'
import { useField } from 'vee-validate'
import { computed, ref, shallowRef, watch } from 'vue'

import type { DropdownOption, OptionsGroup } from '@/components/common/ui/inputs/inputTypes'

import { TEST_IDS_CREATE } from '@/_testIds/testIdsCreate'
import DivideContent from '@/components/common/layout/DivideContent.vue'
import ButtonTertiary from '@/components/common/ui/buttons/ButtonTertiary.vue'
import ActionChip from '@/components/common/ui/inputs/ActionChip.vue'
import TooltipApplier from '@/components/common/ui/overlays/TooltipApplier.vue'
import { translationFunctionDefault } from '@/utilities/translations'

const props = defineProps<{
  allowAddingPills?: boolean
  dataTestid?: string
  disabled?: boolean
  label?: string
  lockedValues?: T[]
  name?: string
  options: OptionsGroup<T>[]
  required?: boolean

  onCreateNewPill?(searchQuery: string): DropdownOption<T>
}>()

const emit = defineEmits<{
  searchChange: [string]
  submit: []
  valueChange: [T[]]
}>()

const tfDefault = translationFunctionDefault()

const { errorMessage: vErrorMessage, value: vValue } = useField<T[] | undefined>(() => {
  return props.name ?? ''
})

const compareOptions = (optionItem: T, otherOptionItem: T): boolean => {
  if (typeof optionItem === 'string' && typeof otherOptionItem === 'string') {
    return optionItem === otherOptionItem
  }
  if (typeof optionItem === 'string' || typeof otherOptionItem === 'string') {
    return false
  }

  return optionItem.name === otherOptionItem.name
}

const model = defineModel<T[]>({
  default: [],
  get: (value) => {
    return props.name ? (vValue.value ?? []) : value
  },
  set: (value) => {
    // Re-add any locked values the user deselected via the dropdown
    const withLocked =
      props.lockedValues && props.lockedValues.length > 0
        ? [
            ...value,
            ...props.lockedValues.filter(
              (locked) => !value.some((val) => compareOptions(val, locked)),
            ),
          ]
        : value
    if (props.name) {
      vValue.value = withLocked
    }

    return withLocked
  },
})

// Options that are selected but no longer in the options - i.e. after options changed but some were selected beforehand
const optionsForOrphanedValues = shallowRef<OptionsGroup<T>[]>([])

const selectedOptions = computed(() => {
  const flatOptions = [...optionsForOrphanedValues.value, ...props.options]
    .flatMap((optionsGroup) => {
      return optionsGroup.options
    })
    // filter out duplicates
    .reduce<DropdownOption<T>[]>((acc, option) => {
      if (
        !acc.some((selectedOption) => {
          return compareOptions(selectedOption.optionValue, option.optionValue)
        })
      ) {
        return [...acc, option]
      }

      return acc
    }, [])

  return (
    flatOptions
      .filter((option) => {
        return model.value.some((selectedOption) => {
          return compareOptions(selectedOption, option.optionValue)
        })
      })
      // since we use the selectedOptions to display the pills, we want them in the same order as the values in model. (Default sort is in order of selection)
      // This enables us to remove the last selected pill via backspace key.
      .sort((current, next) => {
        return (
          model.value.findIndex((selectedValue) => {
            return compareOptions(selectedValue, current.optionValue)
          }) -
          model.value.findIndex((selectedValue) => {
            return compareOptions(selectedValue, next.optionValue)
          })
        )
      })
  )
})

const searchQuery = ref('')

// The browser's default input size; a longer entry widens the input so it wraps into its own row
// instead of scrolling inside the rest of a row that pills already fill.
const MIN_INPUT_SIZE = 20
const inputSize = computed(() => Math.max(MIN_INPUT_SIZE, searchQuery.value.length + 1))

const filteredOptions = computed(() => {
  if (searchQuery.value === '') {
    return props.options
  }

  return props.options
    .map((optionsGroup) => {
      const mappedGroupWithFilteredOptions: OptionsGroup<T> = {
        ...optionsGroup,
        options: optionsGroup.options.filter((option) => {
          return option.optionLabel.toLowerCase().includes(searchQuery.value.toLowerCase())
        }),
      }

      return mappedGroupWithFilteredOptions
    })
    .filter((optionsGroup) => {
      return optionsGroup.options.length > 0
    })
})

const onInputChange = (evt: Event): void => {
  if (evt.target instanceof HTMLInputElement) {
    searchQuery.value = evt.target.value
  }
}

const isLocked = (value: T): boolean =>
  props.lockedValues?.some((lockedValue) => compareOptions(lockedValue, value)) ?? false

const onActionChipClick = (clickEvent: Event, currentOption: DropdownOption<T>): void => {
  if (props.disabled || isLocked(currentOption.optionValue)) {
    return
  }

  clickEvent.preventDefault()
  clickEvent.stopPropagation()
  model.value = model.value.filter((selectedValue) => {
    return !compareOptions(selectedValue, currentOption.optionValue)
  })
  emit('submit')
}

const addPill = (): void => {
  if (props.disabled) {
    return
  }

  const searchValue = searchQuery.value.trim()

  if (!!props.allowAddingPills && !!props.onCreateNewPill && searchValue !== '') {
    const newPillLabel = searchValue

    const pillExists = model.value.some((pill) => {
      if (typeof pill !== 'string') {
        return false
      }

      return pill.toLowerCase() === newPillLabel.toLowerCase()
    })

    const matchingOption = props.options.reduce<DropdownOption<T> | undefined>((acc, group) => {
      const match = group.options.find((opt) => {
        return opt.optionLabel.toLowerCase() === newPillLabel
      })

      return match ?? acc
    }, undefined)

    if (!pillExists) {
      if (matchingOption) {
        model.value = [...model.value, matchingOption.optionValue].filter((val) => {
          return Boolean(val)
        })
      } else {
        const newOption = props.onCreateNewPill(searchQuery.value)

        model.value = [...model.value, newOption.optionValue].filter((val) => {
          return Boolean(val)
        })
      }
    }

    searchQuery.value = ''
  }
}

const handleInputKeydown = (evt: KeyboardEvent): void => {
  if (props.disabled) {
    evt.preventDefault()
    evt.stopPropagation()
    return
  }

  if (evt.key === 'Backspace' && searchQuery.value === '') {
    const lastItem = model.value[model.value.length - 1]
    if (lastItem !== undefined && isLocked(lastItem)) return
    model.value.pop()

    return
  }

  if (evt.key === 'Enter' || evt.code === 'Enter') {
    evt.preventDefault()
    evt.stopPropagation()
    addPill()
  }
  if (evt.key === ' ' || evt.code === 'Space') {
    evt.preventDefault()
    evt.stopPropagation()
    searchQuery.value += ' '
  }
}

const openState = ref(false)
const checkOpenState = (isOpen: boolean): void => {
  if (!isOpen && openState.value) {
    emit('submit')
    openState.value = false
  }
  openState.value = isOpen
}

watch(
  () => model.value,
  (newVal, oldVal) => {
    if (newVal.length > oldVal.length) {
      // If the new value has more items than the old value, it means an item was added
      searchQuery.value = ''
    }
    emit('valueChange', newVal)
  },
)

watch(
  () => {
    return props.options
  },
  (newOptions, oldOptions) => {
    // Evict orphans that are back in the new options so that
    // selectedOptions.includes(option) stays consistent with filteredOptions.
    const newFlatOptions = newOptions.flatMap((group) => group.options)
    optionsForOrphanedValues.value = optionsForOrphanedValues.value
      .map((orphanGroup) => ({
        ...orphanGroup,
        options: orphanGroup.options.filter(
          (orphan) =>
            !newFlatOptions.some((option) =>
              compareOptions(orphan.optionValue, option.optionValue),
            ),
        ),
      }))
      .filter((group) => group.options.length > 0)

    // Find options that are no longer in the new options
    const optionsDiff = oldOptions.flatMap((oldGroup) => {
      const groupIndex = newOptions.findIndex((group) => {
        return group.label === oldGroup.label
      })
      const newGroup = newOptions[groupIndex]

      if (!newGroup) {
        return oldGroup.options
      }

      const groupDiff = oldGroup.options.filter((option) => {
        return !newGroup.options.some((newOption) => {
          return compareOptions(option.optionValue, newOption.optionValue)
        })
      })

      return groupDiff
    })

    // Find options that are selected and no longer in the new options
    // but leave out the ones that have allready been added to this list in past iterations
    const selectedOptionsDiff = optionsDiff
      .filter((option) => {
        return model.value.some((val) => {
          return compareOptions(option.optionValue, val)
        })
      })
      .filter((option) => {
        return !optionsForOrphanedValues.value
          .flatMap((group) => group.options)
          .some((selectedOption) => {
            return compareOptions(option.optionValue, selectedOption.optionValue)
          })
      })

    // Add the orphaned options to the optionsForOrphanedValues
    optionsForOrphanedValues.value = [
      ...optionsForOrphanedValues.value,
      { options: selectedOptionsDiff },
    ]
  },
)

watch(searchQuery, () => {
  emit('searchChange', searchQuery.value)
})

const testIdRoot = computed(() => props.dataTestid ?? TEST_IDS_CREATE.newMultiSelect.root)
</script>

<template>
  <div class="static-or-relative combobox" :data-testid="testIdRoot">
    <Combobox
      v-slot="{ open }"
      v-model="model"
      :by="compareOptions"
      :disabled="props.disabled"
      multiple
    >
      {{ checkOpenState(open) }}
      <ComboboxLabel
        v-if="label"
        class="mb-1.5 inline-block text-sm font-normal"
        :class="[vErrorMessage ? 'text-radical_red-500' : 'text-black-900']"
      >
        {{ label }} {{ required ? '*' : '' }}
      </ComboboxLabel>
      <ComboboxButton
        class="relative flex w-full flex-wrap gap-2 rounded-lg border bg-white px-4 py-2.5"
        :class="[
          vErrorMessage ? 'border-radical_red-500' : 'border-black-200',
          props.disabled ? 'cursor-not-allowed border-black-100 opacity-60' : '',
        ]"
        :data-testid="TEST_IDS_CREATE.newMultiSelect.trigger"
        :disabled="props.disabled"
      >
        <ActionChip
          v-for="(selectedOption, index) in selectedOptions"
          :key="selectedOption.optionLabel ?? index"
          v-bind="{
            ...(isLocked(selectedOption.optionValue)
              ? {}
              : {
                  onIconClick: (clickEvent: Event) => onActionChipClick(clickEvent, selectedOption),
                }),
          }"
          :data-testid="TEST_IDS_CREATE.newMultiSelect.chip"
          :disabled="props.disabled"
          :icon="selectedOption.icon"
          :image="selectedOption.image"
          :label="selectedOption.optionLabel"
        />
        <!-- grow: fill the rest of the row, or a long entry is clipped at the default input width. -->
        <ComboboxInput
          autocomplete="off"
          class="text-normal bg-inherit m-0 max-w-full grow border-none p-0 focus:ring-0"
          :class="[
            props.disabled ? 'cursor-not-allowed text-black-400' : 'text-black-900',
            // Keeps the text clear of the error icon, which overlaps the right padding.
            vErrorMessage ? 'pr-3' : '',
          ]"
          :data-testid="TEST_IDS_CREATE.newMultiSelect.input"
          :disabled="props.disabled"
          :size="inputSize"
          :value="searchQuery"
          @change="onInputChange"
          @keydown="handleInputKeydown"
        />
        <div class="absolute right-2 top-1/2 flex h-4 w-4 -translate-y-1/2">
          <TooltipApplier v-if="vErrorMessage" align="right" is-red :label="vErrorMessage">
            <ExclamationCircleIcon class="h-4 w-4 text-radical_red-500" />
          </TooltipApplier>
        </div>
      </ComboboxButton>
      <ComboboxOptions
        class="options absolute left-0 top-full z-20 mt-1 min-w-full max-w-full rounded-lg !border !border-solid !border-black-50 bg-white py-2.5 text-sm font-normal shadow-lg"
        :data-testid="TEST_IDS_CREATE.newMultiSelect.options"
      >
        <div v-if="filteredOptions.flatMap((o) => o.options).length === 0">
          <ComboboxOption class="text-gray-500 px-4 py-2">
            {{ tfDefault('common:multiSelect.noResults') }}
          </ComboboxOption>
        </div>
        <div
          v-for="(optionsGroup, index) in filteredOptions"
          :key="optionsGroup.label ?? `${index}`"
          class="max-h-96 space-y-1.5 overflow-auto"
        >
          <ComboboxOption
            v-for="option in optionsGroup.options"
            :key="
              typeof option.optionValue === 'string' ? option.optionValue : option.optionValue.name
            "
            class="px-4 py-2 hover:bg-lochmara-50 hover:text-lochmara-600"
            :class="[selectedOptions.includes(option) ? 'bg-lochmara-50 text-lochmara-600' : '']"
            :title="option.optionLabel"
            :value="option.optionValue"
          >
            <div class="flex items-center">
              <component
                :is="option.icon"
                v-if="option.icon"
                class="mr-2 aspect-square w-6"
                :image="option.image"
                :name="option.optionLabel"
                size="small"
              />
              <span class="truncate">
                {{ option.optionLabel }}
              </span>
            </div>
          </ComboboxOption>
        </div>
        <div v-if="props.allowAddingPills && props.onCreateNewPill && searchQuery.trim() !== ''">
          <DivideContent />
          <div class="px-4 pt-2">
            <ButtonTertiary
              :label="tfDefault('common:multiSelect.addButtonLabel', { pillLabel: searchQuery })"
              @click="addPill"
            >
              <PlusCircleIcon />
            </ButtonTertiary>
          </div>
        </div>
      </ComboboxOptions>
    </Combobox>
  </div>
</template>

<style scoped>
.static-or-relative:has(.options) {
  position: relative;
}
</style>
